import React, { Suspense, lazy } from "react";
import {Navigate, Route, Routes} from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthenticatedApp from "./pages/Auth/AuthenticatedApp";
import RoleBasedRedirect from "./components/RoleBasedRedirect";

const CreateProduct = lazy(() =>
  import("./pages/Inventory/Products/CreateProduct")
);
const ProductList = lazy(() =>
  import("./pages/Inventory/Products/ProductList")
);
const TeamPlayerList = lazy(() =>
  import("./pages/Inventory/Products/TeamPlayerList")
);

const AddProductsToWarehouse = lazy(() =>
  import("./pages/Inventory/Warehouse/AddProductsToWarehouse")
);
const OrderQueue = lazy(() =>
  import("./pages/Inventory/Fridges/OrderQueueManagement")
);
const ProductDetail = lazy(() =>
  import("./pages/Inventory/Products/ProductDetail")
);

const FridgeList = lazy(() => import("./pages/Inventory/Fridges/FridgeList"));
const FridgeMap = lazy(() => import("./pages/Inventory/Fridges/FridgeMap"));

const CustomerList = lazy(() =>
  import("./pages/Inventory/Customers/CustomerList")
);
const CustomerCreate = lazy(() =>
  import("./pages/Inventory/Customers/CustomerCreate")
);
const CustomerPage = lazy(() =>
  import("./pages/Inventory/Customers/CustomerPage")
);

const ViewProductsInWarehouse = lazy(() =>
  import("./pages/Inventory/Warehouse/ViewProductsInWarehouse")
);
const AllWarehouses = lazy(() =>
  import("./pages/Inventory/Warehouse/AllWarehouses")
);
const OrderQueueList = lazy(() =>
  import("./pages/Inventory/Fridges/OrderQueueList")
);

const OrderHistory = lazy(() =>
    import("./pages/Inventory/Fridges/OrderHistory")
);
const StealingHistory = lazy(() =>
  import("./pages/Inventory/Fridges/StealingHistory")
);
const PaymentHistory = lazy(() =>
  import("./pages/Inventory/Payment/PaymentHistory")
);

const CategoryList = lazy(() =>
    import("./pages/Inventory/Products/CategoryList")
);

const Orders = lazy(() => import("./pages/Inventory/Fridges/Orders"));
const OrderDetails = lazy(() =>
  import("./pages/Inventory/Fridges/OrderDetails")
);

const FillFridgePageWrapper  = lazy(() => import("./pages/Inventory/Fridges/FillFridgePageWrapper"));
const SettingsFridgePageWrapper  = lazy(() => import("./pages/Inventory/Fridges/SettingsFridgePageWrapper"));

const Overview = lazy(() => import("./pages/Dashboard/Overview"));
const AdminOverview = lazy(() => import("./pages/Dashboard/AdminOverview"));
const AdminAnalytics = lazy(() => import("./pages/Dashboard/AdminAnalytics"));
const TeamPlayerAnalytics = lazy(() => import("./pages/Dashboard/TeamPlayerAnalytics"));

const Inbox = lazy(() => import("./pages/Chat/Inbox"));

const LoginPage = lazy(() => import("./pages/Auth/LoginPage"));
const Users = lazy(() => import("./pages/Auth/Users"));
const ProfilePage = lazy(() => import("./pages/Auth/ProfilePage"));
const Settings = lazy(() => import("./pages/Settings/Settings"));
const Keys = lazy(() => import("./pages/Settings/Keys"));
const Videos = lazy(() => import("./pages/Settings/Videos"));
const Errands = lazy(() => import("./pages/Settings/Errands"));
const UserErrands = lazy(() => import("./pages/Settings/UserErrands"));
const QRCode = lazy(() => import("./pages/Settings/QRCode"));

const ResetPasswordPage = lazy(() => import("./pages/Auth/ResetPasswordPage"));

const AppRoutes = () => (
    <Suspense fallback={<div>Loading...</div>}>
        <Routes>
            {/* Public */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

            {/* Protected layout */}
            <Route
                path="/"
                element={
                    <ProtectedRoute>
                        <AuthenticatedApp />
                    </ProtectedRoute>
                }
            >
                {/* All child paths here MUST be RELATIVE! */}
                <Route index element={<RoleBasedRedirect />} />
                <Route path="dashboard/overview" element={<Overview />} />
                <Route path="dashboard/team_player_analytics" element={<TeamPlayerAnalytics />} />
                <Route path="dashboard/admin_overview" element={<AdminOverview />} />
                <Route path="dashboard/admin_analytics" element={<AdminAnalytics />} />
                <Route path="auth/users" element={<Users />} />
                <Route path="auth/profile" element={<ProfilePage />} />
                <Route path="settings/settings" element={<Settings />} />
                <Route path="settings/keys" element={<Keys />} />
                <Route path="settings/videos" element={<Videos />} />
                <Route path="settings/errands" element={<Errands />} />
                <Route path="settings/usererrands" element={<UserErrands />} />
                <Route path="settings/qrcode" element={<QRCode />} />
                <Route path="inventory/create" element={<CreateProduct />} />
                <Route path="inventory/categories" element={<CategoryList />} />
                <Route path="inventory/teams" element={<TeamPlayerList />} />
                <Route path="inventory/add-products-to-warehouse" element={<AddProductsToWarehouse />} />
                <Route path="inventory/products" element={<ProductList />} />
                <Route path="inventory/all-fridges" element={<FridgeList />} />
                <Route path="inventory/fridges-map" element={<FridgeMap />} />
                <Route path="inventory/order-queue" element={<OrderQueue />} />
                <Route path="product/:productId" element={<ProductDetail />} />
                <Route path="inventory/customerlist" element={<CustomerList />} />
                <Route path="inventory/customercreate" element={<CustomerCreate />} />
                <Route path="customers/:customerId" element={<CustomerPage />} />
                <Route path="inventory/ViewProductsInWarehouse" element={<ViewProductsInWarehouse />} />
                <Route path="inventory/AllWarehouses" element={<AllWarehouses />} />
                <Route path="inventory/fridges/OrderQueueList" element={<OrderQueueList />} />
                <Route path="inventory/fridges/OrderHistory" element={<OrderHistory />} />
                <Route path="inventory/fridges/StealingHistory" element={<StealingHistory />} />
                <Route path="fridge/:fridgeId/fill" element={<FillFridgePageWrapper />} />
                <Route path="fridge/:fridgeId/settings" element={<SettingsFridgePageWrapper />} />
                <Route path="inventory/Payment/PaymentHistory" element={<PaymentHistory />} />
                <Route path="inventory/Fridges/Orders" element={<Orders />} />
                <Route path="inventory/Fridges/Orders/:orderId" element={<OrderDetails />} />
                <Route path="Chat/Inbox" element={<Inbox />} />
            </Route>
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
    </Suspense>
);

export default AppRoutes;
